import {
  field,
  formatEther,
  getDecodedEvent,
  parseUnits,
  readContract,
  writeContractAndWait,
} from "../viem";
import config from "../../config/config";
import MafiaDetectiveAgencyAbi from "../../abis/MafiaDetectiveAgency.json";
import { DETECTIVE_HIRING_TIME } from "../../constants/detective";
import { DetectiveHireStatus } from "../../constants/enum/enum";
import {
  parseDetectiveHireFinishedEvent,
  parseDetectiveHireRequestedEvent,
  parseDetectiveTargetRevealedEvent,
} from "../detective";

export const requestHireDetective = async (
  account: string,
  target: string,
  detectivesCount: number,
  message: string,
  signature: string
) => {
  const tx = await writeContractAndWait({
    address: config.detectiveAgency,
    abi: MafiaDetectiveAgencyAbi,
    functionName: "requestHireDetective",
    args: [target, detectivesCount, JSON.stringify(message), signature],
    account,
  });

  let hireId: number | null = null;

  const event = getDecodedEvent(
    tx,
    MafiaDetectiveAgencyAbi,
    "DetectiveHireRequested"
  );
  if (event) {
    const parsedEvent = parseDetectiveHireRequestedEvent(event);
    hireId = parsedEvent.hireId;
  }

  return {
    transactionHash: tx.transactionHash,
    blockNumber: Number(tx.blockNumber),
    hireId,
  };
};

export const finishHireDetective = async (account: string, hireId: number) => {
  const tx = await writeContractAndWait({
    address: config.detectiveAgency,
    abi: MafiaDetectiveAgencyAbi,
    functionName: "finishHireDetective",
    args: [hireId],
    account,
  });

  const event = getDecodedEvent(
    tx,
    MafiaDetectiveAgencyAbi,
    "DetectiveHireFinished"
  );
  if (event) {
    return parseDetectiveHireFinishedEvent(event);
  }
  return {
    hireId,
    targetNumber: null,
    isTargetFound: false,
    timestamp: null,
  };
};

export const revealTarget = async (account: string, hireId: number) => {
  const tx = await writeContractAndWait({
    address: config.detectiveAgency,
    abi: MafiaDetectiveAgencyAbi,
    functionName: "revealTarget",
    args: [hireId],
    account,
  });

  const event = getDecodedEvent(
    tx,
    MafiaDetectiveAgencyAbi,
    "DetectiveTargetRevealed"
  );
  if (event) {
    return parseDetectiveTargetRevealedEvent(event);
  }

  return {
    blockNumber: Number(tx.blockNumber),
    hireId,
    targetCityId: null,
    canKillUntilTime: null,
    timestamp: null,
  };
};

export const getCityDetectiveCost = async (cityId: number) => {
  const data = await readContract<string>({
    address: config.detectiveAgency,
    abi: MafiaDetectiveAgencyAbi,
    functionName: "getCityDetectiveCost",
    args: [cityId],
  });
  return Number(formatEther(data));
};

export const setCityDetectiveCost = async (
  cityId: number,
  cost: number,
  account: string
) => {
  const costInWei = parseUnits(cost.toString(), 18);
  const data = await writeContractAndWait({
    address: config.detectiveAgency,
    abi: MafiaDetectiveAgencyAbi,
    functionName: "setCityDetectiveCost",
    args: [cityId, costInWei],
    account,
  });
  return data;
};

export const claimCityProfit = async (cityId: number, account: string) => {
  const data = await writeContractAndWait({
    address: config.detectiveAgency,
    abi: MafiaDetectiveAgencyAbi,
    functionName: "claimCityProfit",
    args: [cityId],
    account,
  });
  return data;
};

export const getAgencyInfo = async (cityId: number) => {
  const data: any = await readContract({
    address: config.detectiveAgency,
    abi: MafiaDetectiveAgencyAbi,
    functionName: "cityAgencyInfo",
    args: [cityId],
  });
  return {
    detectiveCost: Number(formatEther(field(data, "detectiveCost", 0))),
    inventoryItemId: Number(field(data, "inventoryItemId", 1)),
    profit: Number(formatEther(field(data, "profit", 2))),
    lastUpdateTime: Number(field(data, "lastUpdateTime", 3)),
  };
};

export const getTargetFoundDuration = async () => {
  const data = await readContract<string>({
    address: config.detectiveAgency,
    abi: MafiaDetectiveAgencyAbi,
    functionName: "targetFoundDuration",
  });
  return Number(data);
};

interface RawDetectiveHire {
  cityId: string | number;
  target: string;
  user: string;
  requestBlock: string | number;
  detectiveCount: string | number;
  startedAt: string | number;
  targetNumber: string | number;
  totalCost: string | number;
  status: string | number;
  isTargetRevealed: boolean;
  targetCityId: string | number;
}

const mapDetectiveHire = (
  hireId: number,
  item: RawDetectiveHire,
  targetFoundDuration: number,
  currentTime: number
) => {
  const rawStatus = Number(field(item, "status", 8));
  const startedAt = Number(field(item, "startedAt", 5));
  const targetNumber = Number(field(item, "targetNumber", 6)) || null;
  const isTargetRevealed = Boolean(field(item, "isTargetRevealed", 9));
  const targetCityId = isTargetRevealed
    ? Number(field(item, "targetCityId", 10))
    : null;
  const detectiveCount = Number(field(item, "detectiveCount", 4));

  let displayStatus: "searching" | "found" | "failed" | "revealed" =
    "searching";
  let canReveal = false;
  const finishedTimestamp: number | null = null;
  let canKillUntilTime: number | null = null;

  if (rawStatus === DetectiveHireStatus.Failed) {
    const revealTime = startedAt + DETECTIVE_HIRING_TIME;
    displayStatus = currentTime >= revealTime ? "failed" : "searching";
  } else if (rawStatus === DetectiveHireStatus.Success) {
    if (isTargetRevealed) {
      displayStatus = "revealed";
      canKillUntilTime =
        startedAt + (targetNumber ?? 0) * 60 + targetFoundDuration;
    } else if (targetNumber !== null && targetNumber > 0) {
      const foundTime = startedAt + targetNumber * 60;
      if (currentTime >= foundTime) {
        displayStatus = "found";
        canReveal = true;
      }
    }
  }

  return {
    id: hireId,
    name: detectiveCount > 1 ? `${detectiveCount} Detectives` : "1 Detective",
    target: String(field(item, "target", 1)),
    targetAddress: String(field(item, "target", 1)),
    targetName: "",
    status: displayStatus,
    rawStatus,
    requestedTimestamp: startedAt,
    finishedTimestamp,
    targetNumber,
    isTargetRevealed,
    canReveal,
    targetCityId,
    canKillUntilTime,
  };
};

/** Load every detective hire belonging to the connected user. */
export const getUserDetectiveHires = async (
  account: string,
  signMsg: string,
  signature: string
) => {
  const targetFoundDuration = await getTargetFoundDuration();
  const currentTime = Math.floor(Date.now() / 1000);
  const allHires: ReturnType<typeof mapDetectiveHire>[] = [];
  const pageSize = 20;
  let startIndex = 0;

  while (true) {
    const result: any = await readContract({
      address: config.detectiveAgency,
      abi: MafiaDetectiveAgencyAbi,
      functionName: "getUserDetectiveHires",
      args: [
        account,
        startIndex,
        pageSize,
        JSON.stringify(signMsg),
        signature,
      ],
      account,
    });

    const hireIds: (string | number)[] = field(result, "hireIds", 0) ?? [];
    const list: RawDetectiveHire[] = field(result, "list", 1) ?? [];

    if (hireIds.length === 0) break;

    for (let index = 0; index < hireIds.length; index += 1) {
      if (!list[index]) continue;
      allHires.push(
        mapDetectiveHire(
          Number(hireIds[index]),
          list[index],
          targetFoundDuration,
          currentTime
        )
      );
    }

    if (hireIds.length < pageSize) break;
    startIndex += pageSize;
  }

  return allHires.reverse();
};
